<template>
    <div class="h-100">
        <order-timeline-box
            class="h-100"
            :class="{'bg-light-info': !isComplete}"
            :title="tileTitle"
            :message="tileMessage"
            :timestamp="timestamp"
            :complete="isComplete"
            :icon="tileIcon"
        >
            <button v-if="order.state !== OrderState.ReadyForPickup"
                    class="btn btn-info"
                    @click="showAddShippingDetails = true"
                    :disabled="logsByState.hasOwnProperty(OrderState.Refunded)"
            >
                <i class="fas fa-truck-moving"/>
                {{ __('actions.select_thing', {thing: __('orders.shipping.provider')}) }}
            </button>
            <button v-else class="btn btn-info"
                    @click="$emit('transition', OrderState.PickedUp)"
                    :disabled="logsByState.hasOwnProperty(OrderState.Refunded)"
            >
                <i class="fas fa-people-arrows"/>
                {{ __('actions.order.transition_to.collected') }}
            </button>
        </order-timeline-box>

        <Modal v-if="!isComplete && !logsByState.hasOwnProperty(OrderState.ReadyForPickup)"
               :visible="showAddShippingDetails"
               @update:visible="v => showAddShippingDetails = v"
        >
            <template v-slot:title>
                {{ __('actions.provide_shipping_details') }}
            </template>
            <template v-slot:subtitle>
                {{ __('actions.choose_shipping_provider_or_enter_custom') }}
            </template>
            <template v-slot:default>
                <SelectShippingProviderTabs v-show="shippingProviders.length > 0"
                                            :shipping-providers="shippingProviders"
                                            v-model="selectedShippingProvider"
                />
                <TransitionGroup name="bounce">
                    <GarantiertGeliefertShippingForm v-if="selectedShippingProvider?.code === 'GG'"
                                                     :class="{'mt-5': shippingProviders.length > 0}"
                                                     :order="order"
                                                     :store="store"
                                                     key="GG"
                    />
                    <GenericShippingForm v-else-if="selectedShippingProvider === 'custom'"
                                         :class="{'mt-5': shippingProviders.length > 0}"
                                         :order="order"
                                         :store="store"
                                         key="custom"
                                         @transition="toState => $emit('transition', toState)"
                    />
                </TransitionGroup>
            </template>
        </Modal>
    </div>
</template>

<script>
import {OrderState} from '@composables/orderStates';
import AddShippingDetailsModal from "@components/Orders/Modals/AddShippingDetailsModal.vue";
import OrderTimelineBox from "@components/Orders/Timeline/OrderTimelineBox.vue";
import Modal from "@components/Modal.vue";
import Dot from "@components/Dot.vue";
import IntegrationIcon from "@components/Integrations/IntegrationIcon.vue";
import { Link } from '@inertiajs/vue2';
import Checkbox from "@components/Form/Checkbox.vue";
import SelectPackageDimensionPreset from "@components/Packages/SelectPackageDimensionPreset.vue";
import GarantiertGeliefertShippingForm
    from "@components/Orders/Modals/Forms/Shipping/GarantiertGeliefertShippingForm.vue";
import GenericShippingForm from "@components/Orders/Modals/Forms/Shipping/GenericShippingForm.vue";
import SelectShippingProviderTabs from "@components/Orders/Modals/SelectShippingProviderTabs.vue";
import TileOrderPickedUp from "@components/Orders/Timeline/Tiles/TileOrderPickedUp.vue";

export default {
    components: {
        TileOrderPickedUp,
        SelectShippingProviderTabs,
        GenericShippingForm,
        GarantiertGeliefertShippingForm,
        SelectPackageDimensionPreset,
        Checkbox,
        IntegrationIcon, Dot, Modal, AddShippingDetailsModal, OrderTimelineBox, Link
    },

    props: {
        store: Object,
        order: Object,
        users: Object,
        shippingProviders: Array,
    },

    data() {
        return {
            showAddShippingDetails: false,
            selectedShippingProvider: null,
        };
    },

    computed: {
        OrderState() {
            return OrderState;
        },

        logsByState(){
            return this.order.logs_by_state;
        },

        tileTitle() {
            if (this.isGarantiertGeliefertDelivery && this.isComplete) {
                return 'UNTERWEGS';
            }

            return this.__('orders.states.' + this.order.state)
        },

        tileMessage() {
            if (this.isGarantiertGeliefertDelivery) {
                return 'Garantiert Geliefert';
            }

            if (this.logsByState.hasOwnProperty(OrderState.Send)) {
                return this.users[this.logsByState[OrderState.Send].issuer_uid]
            }

            return '';
        },

        tileIcon() {
            if (this.isGarantiertGeliefertDelivery) {
                return 'fa-truck-moving';
            }

            return 'fa-paper-plane';
        },

        timestamp() {
            if (this.logsByState.hasOwnProperty(OrderState.Send)) {
                return this.logsByState[OrderState.Send].created_at;
            } else if (this.logsByState.hasOwnProperty(OrderState.PickedUp)) {
                return this.logsByState[OrderState.PickedUp].created_at;
            }

            return ''
        },

        isGarantiertGeliefertDelivery() {
            if (this.order.shipping_details.length === 0) {
                return false;
            }

            return this.order.shipping_details[0].transport_service === 'GG';
        },

        isComplete() {
            return this.logsByState.hasOwnProperty(OrderState.Send)
                || this.logsByState.hasOwnProperty(OrderState.PickedUp)
                || this.logsByState.hasOwnProperty(OrderState.Cancelled)
                || this.logsByState.hasOwnProperty(OrderState.Rejected)
                || this.logsByState.hasOwnProperty(OrderState.Refunded);
        }
    },
};
</script>

<style scoped>
.bounce-enter-active {
    animation: bounce-in 0.26s ease-out;
}

.bounce-leave-active {
    transition: all 0s linear;
}

.bounce-leave-to {
    opacity: 0;
}

@keyframes bounce-in {
    0% {
        transform: scale(0.95);
    }
    100% {
        transform: scale(1);
    }
}
</style>
